import { useState, useEffect } from 'react';
import {
  AlertCircle, Loader, Eye, EyeOff, Lock, Mail, User, 
  Sparkles, Zap, CheckCircle, Bot
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../components/auth/AuthContext';
import { authService } from '../../services/PlatformService';

const UserRegistration = () => {
  const navigate = useNavigate();
  const { currentUser, login } = useAuth();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');

  useEffect(() => {
    if (currentUser) {
      navigate('/account');
    }
  }, [currentUser, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    if (errors[name]) {
      setErrors(prevErrors => ({ ...prevErrors, [name]: '' }));
    }
    if (apiError) {
      setApiError('');
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Nome é obrigatório';
    if (!formData.email.trim()) {
      newErrors.email = 'E-mail é obrigatório';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'E-mail inválido';
    }
    if (!formData.password) {
      newErrors.password = 'Senha é obrigatória';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Senha deve ter pelo menos 8 caracteres';
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Senhas não coincidem';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!validate()) return;
    setIsSubmitting(true);
    setApiError('');
    try {
      const { confirmPassword, ...apiData } = formData;
      const response = await authService.register(apiData);
      if (response && response.data) {
        login(response.data.user, response.data.token);
        navigate('/account');
      } else {
        throw new Error('Resposta da API inválida após registro.');
      }
    } catch (error) {
      let errorMessage = 'Ocorreu um erro ao criar sua conta. Por favor, tente novamente.';
      if (error.response && error.response.data && error.response.data.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      setApiError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-blue-50 relative overflow-hidden">
      {/* Background decorativo com gradientes */}
      <div className="absolute inset-0">
        <div className="absolute top-0 -left-40 w-80 h-80 bg-gradient-to-br from-indigo-500/20 to-indigo-500/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 -right-40 w-80 h-80 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center py-20 px-4">
        <div className="max-w-2xl w-full grid lg:grid-cols-1 gap-8 items-center">

          {/* Lado direito - Formulário de cadastro */}
          <div>
            <div className="bg-white/80 backdrop-blur-sm shadow-2xl rounded-3xl p-8 md:p-10 border border-gray-100">
              {/* Mostrar erro */}
              {apiError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-5 py-4 rounded-2xl flex items-start mb-6">
                  <AlertCircle className="w-6 h-6 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-base">{apiError}</span>
                </div>
              )}

              <form className="space-y-5" onSubmit={handleSubmit}>
                <div>
                  <label className="block text-base font-semibold text-gray-700 mb-2">
                    Seu nome completo
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-4 text-gray-400">
                      <User size={22} />
                    </span>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`w-full pl-12 pr-5 py-4 text-lg border-2 ${
                        errors.name ? 'border-red-300' : 'border-gray-200'
                      } rounded-2xl focus:outline-none focus:ring-4 focus:ring-green-100 focus:border-green-500 transition-all`}
                      placeholder="João Silva"
                    />
                  </div>
                  {errors.name && <p className="text-red-500 text-sm mt-2">{errors.name}</p>}
                </div>

                <div>
                  <label className="block text-base font-semibold text-gray-700 mb-2">
                    Seu melhor e-mail
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-4 text-gray-400">
                      <Mail size={22} />
                    </span>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full pl-12 pr-5 py-4 text-lg border-2 ${
                        errors.email ? 'border-red-300' : 'border-gray-200'
                      } rounded-2xl focus:outline-none focus:ring-4 focus:ring-green-100 focus:border-green-500 transition-all`}
                      placeholder="seu@email.com"
                    />
                  </div>
                  {errors.email && <p className="text-red-500 text-sm mt-2">{errors.email}</p>}
                </div>

                <div>
                  <label className="block text-base font-semibold text-gray-700 mb-2">
                    Crie uma senha forte
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-4 text-gray-400">
                      <Lock size={22} />
                    </span>
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className={`w-full pl-12 pr-12 py-4 text-lg border-2 ${
                        errors.password ? 'border-red-300' : 'border-gray-200'
                      } rounded-2xl focus:outline-none focus:ring-4 focus:ring-green-100 focus:border-green-500 transition-all`}
                      placeholder="Mínimo 8 caracteres"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400 hover:text-gray-600 transition-colors"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff size={22} /> : <Eye size={22} />}
                    </button>
                  </div>
                  {errors.password && <p className="text-red-500 text-sm mt-2">{errors.password}</p>}
                </div>

                <div>
                  <label className="block text-base font-semibold text-gray-700 mb-2">
                    Confirme sua senha
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-4 text-gray-400">
                      <Lock size={22} />
                    </span>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className={`w-full pl-12 pr-12 py-4 text-lg border-2 ${
                        errors.confirmPassword ? 'border-red-300' : 'border-gray-200'
                      } rounded-2xl focus:outline-none focus:ring-4 focus:ring-green-100 focus:border-green-500 transition-all`}
                      placeholder="Digite a senha novamente"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400 hover:text-gray-600 transition-colors"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <EyeOff size={22} /> : <Eye size={22} />}
                    </button>
                  </div>
                  {errors.confirmPassword && <p className="text-red-500 text-sm mt-2">{errors.confirmPassword}</p>}
                </div>

                <div className="space-y-4 pt-2">
                  <button
                    type="submit"
                    className="w-full py-4 px-6 border border-transparent rounded-2xl shadow-lg text-white text-lg font-semibold bg-gradient-to-r from-green-800 to-green-600 hover:from-green-700 hover:to-green-700 focus:outline-none focus:ring-4 focus:ring-indigo-200 transition-all transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader className="w-6 h-6 mr-3 animate-spin" />
                        Criando sua conta...
                      </>
                    ) : (
                      'Criar conta'
                    )}
                  </button>

                </div>
              </form>

              <div className="mt-8 text-center">
                <p className="text-lg text-gray-600">
                  Já tem uma conta?{' '}
                  <Link to="/login" className="font-semibold text-green-600 hover:text-green-700 hover:underline transition-colors">
                    Faça login aqui
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserRegistration;